/**
 * Text size, chosen inside the app.
 *
 * A web page normally inherits the reader's own settings. This one cannot:
 *
 *  - Installed to the home screen, the app runs with no browser chrome at all,
 *    so there is no aA menu and no page zoom. The single most common way a
 *    person enlarges text on a phone is simply not available here.
 *  - iOS Dynamic Type does not reach web content, so a driver who has set their
 *    whole phone to large text still gets our default.
 *  - On Android, Chrome's own font boosting is switched off by
 *    `-webkit-text-size-adjust`, which exists to stop unwanted inflation in
 *    landscape and takes the accessibility scaling with it.
 *
 * So the app has to offer it. Every size in the stylesheet is in rem, which
 * means moving the root size moves the entire interface together — buttons,
 * spacing and all — rather than producing overlapping text at a larger size.
 *
 * Applied before React renders, from main.tsx, so a driver who chose large text
 * never sees a frame of small text first.
 */

export type TextSize = 'normal' | 'large' | 'xlarge';

const KEY = 'jw.textSize';

/* 16px, 18px, 20.8px at the root. Deliberately modest steps: past about 130%
   the stop cards stop fitting a phone screen and a driver has to scroll to see
   one hospital, which helps nobody. */
const SCALE: Record<TextSize, string> = {
  normal: '100%',
  large: '112.5%',
  xlarge: '130%',
};

export function getTextSize(): TextSize {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'large' || v === 'xlarge' || v === 'normal') return v;
  } catch {
    /* private mode, or storage disabled — fall through to the default */
  }
  return 'normal';
}

export function applyTextSize(size: TextSize): void {
  document.documentElement.style.fontSize = SCALE[size];
  try {
    localStorage.setItem(KEY, size);
  } catch {
    // Not being able to remember the choice must not stop it applying now.
  }
}

/** Called once at boot, before the first render. */
export function initTextSize(): void {
  applyTextSize(getTextSize());
}
